import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Close, CloudUpload, Image, VideoFile } from "@mui/icons-material";
import {
  Alert,
  Fade,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { INewLesson } from "../../../../@types";
import { Modal } from "../../../../components";
import { useInputText } from "../../../../hooks";
import services from "../../../../services";
import { useData as useModulesData } from "../../modules/use-data";
import { ModalRegisterProps } from "../@types";
import { useData } from "../use-data";
import { IconPreview } from "./preview";

import { Checkbox } from "@/components/ui/checkbox";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import Uppy from "@uppy/core";
import Tus from "@uppy/tus";
import { Loader2 } from "lucide-react";

// Adicionar enum para steps
const STEPS = [
  { key: "create", label: "Criar aula" },
  { key: "thumbnail", label: "Carregar miniatura" },
  { key: "video", label: "Carregar vídeo" },
  { key: "finish", label: "Finalizar" },
];

export default function ModalRegister({ open, onClose }: ModalRegisterProps) {
  const { modules } = useModulesData();
  const { refetch } = useData();
  const initialValues = { title: "", description: "" };
  const input = useInputText(initialValues);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  // Uppy será criado dinamicamente para cada upload

  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [video, setVideo] = useState<File | null>(null);
  const [moduleId, setModuleId] = useState<string | null>(
    modules ? modules[0]?.id : null
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);

  const [loading, setLoading] = useState<{
    loading: boolean;
    status?: string;
    progress?: number;
  }>({ loading: false });
  const [error, setError] = useState<string | null>(null);

  // Adicionar estado para steps
  const [stepStatus, setStepStatus] = useState<{
    [key: string]: "pending" | "in_progress" | "done" | "error";
  }>({});

  // Estados para cancelamento
  const [createAbortController, setCreateAbortController] =
    useState<AbortController | null>(null);
  const [thumbnailAbortController, setThumbnailAbortController] =
    useState<AbortController | null>(null);

  // eslint-disable-next-line
  const [uppyInstance, setUppyInstance] = useState<Uppy | null>(null);

  // Estado para o campo opcional position
  const [position, setPosition] = useState<number | "" | undefined>("");
  // Estado para mostrar/ocultar secção avançado
  const [showAdvanced, setShowAdvanced] = useState(false);

  const resetStates = useCallback(() => {
    input.clear();
    setThumbnail(null);
    setVideo(null);
    setVideoPreview(null);
    setModuleId(modules ? modules[0]?.id : null);
    setError(null);
    setLoading({ loading: false, status: undefined });
    setIsDragOver(false);
    setStepStatus({});
    setPosition(""); // Limpar position
    setShowAdvanced(false); // Ocultar secção avançado
    // Limpar os inputs de arquivo
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = "";
    }
  }, [modules]);

  const setStep = (
    stepKey: string,
    status: "pending" | "in_progress" | "done" | "error"
  ) => {
    setStepStatus((prev) => ({ ...prev, [stepKey]: status }));
  };

  const onModuleIdChange = (event: SelectChangeEvent) =>
    setModuleId(event.target.value);

  const onThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files: newFiles } = event.currentTarget;

    if (newFiles && newFiles[0]) {
      const file = newFiles[0];
      // Verificar se é uma imagem válida
      if (file.type.startsWith("image/")) {
        setThumbnail(file);
      } else {
        toast.error(
          "Por favor, selecione um arquivo de imagem válido (JPG, PNG)"
        );
        // Limpar o input se o arquivo não for válido
        if (thumbnailInputRef.current) {
          thumbnailInputRef.current.value = "";
        }
      }
    } else {
      setThumbnail(null);
    }
  };

  const onVideoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files: newFiles } = event.currentTarget;

    if (newFiles) {
      const videoFile = newFiles[0];
      handleVideoFile(videoFile);
    } else {
      setVideo(null);
      setVideoPreview(null);
    }
  };

  const handleVideoFile = (videoFile: File) => {
    if (videoFile.type.startsWith("video/")) {
      setVideo(videoFile);
      const previewUrl = URL.createObjectURL(videoFile);
      setVideoPreview(previewUrl);

      // Adicionar arquivo ao Uppy (será feito no momento do upload)
    } else {
      toast.error("Por favor, selecione um arquivo de vídeo válido");
      // Limpar o input se o arquivo não for válido
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const videoFile = files.find((file) => file.type.startsWith("video/"));

    if (videoFile) {
      handleVideoFile(videoFile);
      // Limpar o input de arquivo para manter consistência
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } else {
      toast.error("Por favor, arraste um arquivo de vídeo válido");
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const removeVideo = () => {
    setVideo(null);
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
      setVideoPreview(null);
    }
    // Limpar o input de arquivo para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeThumbnail = () => {
    setThumbnail(null);
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = "";
    }
  };

  const closeModal = useCallback(() => {
    if (stepStatus.create === "in_progress" && createAbortController) {
      createAbortController.abort();
    }
    if (stepStatus.thumbnail === "in_progress" && thumbnailAbortController) {
      thumbnailAbortController.abort();
    }
    if (stepStatus.video === "in_progress" && uppyInstance) {
      uppyInstance.cancelAll();
    }
    resetStates();
    onClose();
    refetch();
  }, [
    onClose,
    refetch,
    resetStates,
    stepStatus,
    createAbortController,
    thumbnailAbortController,
    uppyInstance,
  ]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      console.log("🚀 Iniciando handleSubmit...");
      event?.preventDefault();
      if (error) setError(null);

      // Fase 1: Processando dados iniciais
      console.log("📋 Fase 1: Processando dados iniciais");
      setLoading({
        loading: true,
        status: "Preparando para criar a aula...",
        progress: 0,
      });

      const module = modules!.find(({ id }) => id === moduleId);
      console.log("📦 Módulo encontrado:", module);

      const { title, description } = input;
      console.log("📝 Dados da aula:", { title, description });

      const data: INewLesson = {
        title,
        description,
        moduleId: module?.id,
        // Só incluir position se a secção avançado estiver visível e for um número válido
        ...(showAdvanced && position !== undefined && position !== ""
          ? { position: Number(position) }
          : {}),
        video: {
          contentType: video?.type,
          filename: video?.name,
          size: video?.size,
        },
      };

      console.log("📊 Dados preparados:", data);

      if (thumbnail) {
        console.log("🖼️ Thumbnail encontrada:", {
          name: thumbnail.name,
          size: formatFileSize(thumbnail.size),
          type: thumbnail.type,
        });

        if (thumbnail.size > 20 * 1024 * 1024) {
          console.error(
            "❌ Thumbnail muito grande:",
            formatFileSize(thumbnail.size)
          );
          throw new Error("O tamanho da capa não pode ser maior que 20MB");
        }

        data.thumbnail = {
          contentType: thumbnail.type,
          filename: thumbnail.name,
          size: thumbnail.size,
        };
      }

      // Fase 2: Criando a aula na API
      console.log("🌐 Fase 2: Criando a aula na API");
      setLoading({
        loading: true,
        status: "A criar novo registo de aula...",
        progress: 10,
      });
      setStep("create", "in_progress");
      const createController = new AbortController();
      setCreateAbortController(createController);

      const createdLesson = await services.api
        .post("/lessons", data, { signal: createController.signal })
        .then(({ data }) => data?.data)
        .catch((err) => {
          if (err.name === "CanceledError" || err.name === "AbortError")
            throw new Error("Cancelado pelo utilizador");
          throw err;
        });
      setCreateAbortController(null);
      setStep("create", "done");

      if (createdLesson) {
        if (createdLesson.thumbnailUploadURL && thumbnail) {
          setStep("thumbnail", "in_progress");
          const thumbController = new AbortController();
          setThumbnailAbortController(thumbController);
          try {
            // Fase 3: Carregando a miniatura
            console.log("🖼️ Fase 3: Carregando a miniatura");
            setLoading({
              loading: true,
              status: "A carregar miniatura da aula...",
              progress: 20,
            });

            console.log(
              "📤 URL de upload da thumbnail:",
              createdLesson.thumbnailUploadURL
            );

            await axios
              .put(createdLesson.thumbnailUploadURL, thumbnail, {
                headers: { "Content-Type": thumbnail.type },
                signal: thumbController.signal,
                onUploadProgress: (progressEvent) => {
                  const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / (progressEvent.total || 1)
                  );
                  console.log(`📈 Progresso thumbnail: ${percentCompleted}%`);
                  // Progresso de 20% a 50%
                  const adjustedProgress =
                    20 + Math.floor(percentCompleted * 0.3);
                  setLoading((prev) => ({
                    ...prev,
                    status: `A carregar miniatura: ${percentCompleted}%`,
                    progress: adjustedProgress,
                  }));
                },
              })
              .catch((err) => {
                if (err.name === "CanceledError" || err.name === "AbortError")
                  throw new Error("Cancelado pelo utilizador");
                throw err;
              });

            console.log("✅ Thumbnail carregada com sucesso");
            setLoading({
              loading: true,
              status: "Miniatura carregada com sucesso",
              progress: 50,
            });
            setStep("thumbnail", "done");
            setThumbnailAbortController(null);
            toast.success("Miniatura guardada com sucesso");
          } catch (error) {
            console.error("❌ Erro ao carregar thumbnail:", error);
            throw new Error(
              "Falha ao guardar a miniatura.\n" + (error as Error).message
            );
          }
        } else {
          console.log("⏭️ Sem thumbnail, avançando para vídeo");
          // Se não tiver miniatura, avança direto para 50%
          setLoading({
            loading: true,
            status: "A preparar o carregamento do vídeo...",
            progress: 50,
          });
          setStep("thumbnail", "done");
        }

        if (video) {
          setStep("video", "in_progress");
          // Criar Uppy e guardar referência
          const uploadUppy = new Uppy({
            autoProceed: false,
            restrictions: { maxNumberOfFiles: 1 },
            debug: true,
          });
          setUppyInstance(uploadUppy);
          try {
            // Fase 4: Carregando o vídeo
            console.log("🎥 Fase 4: Carregando o vídeo");
            setLoading({
              loading: true,
              status: "A iniciar carregamento do vídeo...",
              progress: 50,
            });

            console.log(
              "📤 URL de upload do vídeo:",
              createdLesson.videoUploadURL
            );
            console.log("📁 Configurando Uppy com TUS...");

            // Configurar TUS apenas uma vez
            const calculateChunkSize = (fileSize: number): number => {
              // Para ficheiros pequenos (< 100MB): 1MB
              if (fileSize < 100 * 1024 * 1024) {
                return 1 * 1024 * 1024;
              }
              // Para ficheiros médios (100MB - 500MB): 5MB
              if (fileSize < 500 * 1024 * 1024) {
                return 5 * 1024 * 1024;
              }
              // Para ficheiros grandes (> 500MB): 10MB
              return 10 * 1024 * 1024;
            };

            uploadUppy.use(Tus, {
              endpoint: createdLesson.videoUploadURL,
              chunkSize: calculateChunkSize(video.size),
              retryDelays: [0, 1000, 3000, 5000],
            });

            // Adicionar o ficheiro ao Uppy
            await uploadUppy.addFile({
              name: video.name,
              type: video.type,
              data: video,
            });

            // Configurar eventos de progresso
            uploadUppy.on("upload-progress", (_, progress) => {
              const percentCompleted = Math.round(
                (progress.bytesUploaded * 100) / (progress.bytesTotal || 1)
              );
              console.log(
                `📈 Progresso vídeo: ${percentCompleted}% (${formatFileSize(progress.bytesUploaded)}/${formatFileSize(progress.bytesTotal || 0)})`
              );
              // Progresso de 50% a 95%
              const adjustedProgress = 50 + Math.floor(percentCompleted * 0.45);
              setLoading((prev) => ({
                ...prev,
                status: `A carregar vídeo: ${percentCompleted}%`,
                progress: adjustedProgress,
              }));
            });

            // Configurar evento de sucesso
            uploadUppy.on("upload-success", (_, response) => {
              console.log(
                "✅ Upload do vídeo concluído com sucesso:",
                response
              );
              setLoading({
                loading: true,
                status: "Vídeo carregado com sucesso!",
                progress: 95,
              });
              toast.success("Vídeo guardado com sucesso");
            });

            // Configurar evento de erro
            uploadUppy.on("upload-error", (_, error, response) => {
              console.error("❌ Erro no upload do vídeo:", error, response);
              throw new Error(`Falha no upload do vídeo: ${error.message}`);
            });

            console.log("🚀 Iniciando upload do vídeo...");
            // Fazer upload
            const uploadResult = await uploadUppy.upload();
            setUppyInstance(null);
            console.log("📊 Resultado do upload:", uploadResult);

            if (
              uploadResult &&
              uploadResult.failed &&
              uploadResult.failed.length > 0
            ) {
              console.error("❌ Upload falhou:", uploadResult.failed);
              throw new Error("Falha no upload do vídeo");
            }

            // Fase 5: Finalizando
            console.log("✅ Fase 5: Finalizando");
            setLoading({
              loading: true,
              status: "Vídeo carregado com sucesso! A finalizar...",
              progress: 95,
            });
            setStep("video", "done");
          } catch (error) {
            setUppyInstance(null);
            console.error("❌ Erro no upload do vídeo:", error);
            throw new Error(
              "Falha ao guardar o vídeo.\n" + (error as Error).message
            );
          }
        }

        // Fase 6: Concluído
        console.log("🎉 Fase 6: Concluído");
        setLoading({
          loading: true,
          status: "Aula criada com sucesso!",
          progress: 100,
        });
        setStep("finish", "in_progress");

        // Pequeno atraso para mostrar mensagem de conclusão
        console.log("⏳ Aguardando 800ms para mostrar conclusão...");
        await new Promise((resolve) => setTimeout(resolve, 800));
      }

      console.log("🔚 Fechando modal...");
      closeModal();
    } catch (error) {
      console.error("💥 Erro geral no handleSubmit:", error);
      setError((error as Error).message);
      // Marcar o step atual como erro
      const currentStep = Object.keys(stepStatus).find(
        (k) => stepStatus[k] === "in_progress"
      );
      if (currentStep) setStep(currentStep, "error");
    } finally {
      console.log("🧹 Limpando estado de loading...");
      setLoading({ loading: false, status: undefined, progress: 0 });
      setStep("finish", "done");
      setCreateAbortController(null);
      setThumbnailAbortController(null);
      setUppyInstance(null);
    }
  };

  useEffect(() => {
    if (modules && modules.length > 0) setModuleId(modules[0]?.id);
  }, [modules]);

  useEffect(() => {
    if (open) {
      resetStates();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modules]);

  return (
    <Modal
      open={open}
      onClose={closeModal}
      className="w-full max-w-[90%] md:max-w-[80%] lg:max-w-[1020px] max-h-[95%]"
      head={<h2 className="text-xl font-bold">Registar nova Lição</h2>}
    >
      <Fade in={open}>
        <div className="text-xl grid gap-8">
          {loading.loading ? (
            <div className="flex flex-col items-center justify-center py-10 space-y-6">
              {/* Steps UI */}
              <div className="w-full  max-w-md text-center mb-6">
                <ol className="grid grid-cols-2 gap-3">
                  {STEPS.map((step, idx) => {
                    const status =
                      stepStatus[step.key] ||
                      (idx === 0 ? "in_progress" : "pending");
                    let icon = <HourglassEmptyIcon className="text-gray-400" />;
                    if (status === "done")
                      icon = <CheckCircleIcon className="text-green-600" />;
                    if (status === "error")
                      icon = <ErrorIcon className="text-red-600" />;
                    if (status === "in_progress")
                      icon = <Loader2 className="size-5 animate-spin" />;
                    return (
                      <li key={step.key} className="flex items-center gap-2">
                        {icon}
                        <span
                          className={`text-base ${status === "done" ? "text-green-700" : status === "error" ? "text-red-700" : status === "in_progress" ? "text-primary" : "text-gray-500"}`}
                        >
                          {step.label}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
              <div className="w-full max-w-md text-center">
                <h3 className="text-lg font-medium mb-4">
                  {loading.status || "A processar..."}
                </h3>

                <LinearProgress
                  variant="determinate"
                  value={loading.progress || 0}
                  sx={{ height: 10, borderRadius: 5 }}
                />

                <p className="mt-2 text-sm">
                  {loading.progress ? `${loading.progress}%` : ""}
                </p>
              </div>

              {error && (
                <Alert
                  severity="error"
                  className="w-full max-w-md"
                  sx={{ mt: 2 }}
                >
                  {error}
                </Alert>
              )}
              {/* Botão Cancelar só durante a criação da aula */}
              {stepStatus.create === "in_progress" && (
                <div className="w-full max-w-[250px] flex justify-center  mt-4">
                  <Button
                    variant="outline"
                    onClick={closeModal}
                    className="w-full py-3 h-fit"
                    size="sm"
                  >
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-6 h-full w-full">
              {/* Layout principal - duas colunas sem scroll geral */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 h-full w-full">
                <div className="space-y-6 lg:col-span-2">
                  <ScrollArea className="flex-1 h-[500px] w-full">
                    {/* Seção de Upload do Vídeo */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Vídeo da Lição</h3>

                      {!video ? (
                        <div
                          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer ${
                            isDragOver
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-300 hover:border-gray-400"
                          }`}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <CloudUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                          <div className="space-y-2">
                            <p className="text-lg font-medium">
                              Arraste e largue o vídeo aqui
                            </p>
                            <p className="text-sm text-gray-500">
                              ou clique para selecionar um arquivo
                            </p>
                            {/* <p className="text-xs text-gray-400">MP4 até 200MB</p> */}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Informações do arquivo */}
                          <div className="border rounded-lg px-4 pt-2 pb-0 ">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <VideoFile className="h-6 w-6 text-blue-500" />
                                <div>
                                  <p className="font-medium text-sm">
                                    {video.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(video.size)}
                                  </p>
                                </div>
                              </div>
                              <IconButton onClick={removeVideo} size="small">
                                <Close />
                              </IconButton>
                            </div>
                          </div>

                          {/* Preview do vídeo */}
                          {videoPreview && (
                            <div className="border rounded-lg overflow-hidden bg-black">
                              <video
                                src={videoPreview}
                                className="w-full aspect-video object-contain"
                                controls
                              />
                            </div>
                          )}
                        </div>
                      )}

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*"
                        onChange={onVideoChange}
                        className="hidden"
                      />
                    </div>

                    {/* Seção de Thumbnail */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">
                        Miniatura da Lição
                      </h3>

                      {!thumbnail ? (
                        <div
                          className="border-2 border-dashed rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
                          onClick={() => thumbnailInputRef.current?.click()}
                        >
                          <Image className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">
                            Clique para adicionar uma miniatura
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            JPG, PNG até 20MB
                          </p>
                        </div>
                      ) : (
                        <div className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <Image className="h-5 w-5 text-green-500" />
                              <span className="text-sm font-medium">
                                Miniatura selecionada
                              </span>
                            </div>
                            <IconButton onClick={removeThumbnail} size="small">
                              <Close />
                            </IconButton>
                          </div>
                          <div className="relative aspect-video  rounded overflow-hidden">
                            <IconPreview
                              alt="pré-visualização da miniatura da lição"
                              file={thumbnail}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {thumbnail.name} ({formatFileSize(thumbnail.size)})
                          </p>
                        </div>
                      )}

                      <input
                        ref={thumbnailInputRef}
                        type="file"
                        accept=".jpg, .png"
                        onChange={onThumbnailChange}
                        className="hidden"
                      />
                    </div>
                  </ScrollArea>
                </div>

                {/* Coluna da direita - Informações (com scroll) */}
                <div className="lg:col-span-3 flex flex-col gap-4 h-full">
                  <ScrollArea className="flex-1 h-[600px] pr-4">
                    <h3 className="text-lg font-semibold mb-6">
                      Informações da Lição
                    </h3>
                    {/* Seção de Status/Progresso */}
                    <div className="space-y-3 mb-3">
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span>Vídeo:</span>
                          <span
                            className={
                              video ? "text-green-600" : "text-gray-400"
                            }
                          >
                            {video ? "✓ Selecionado" : "Não selecionado"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Miniatura:</span>
                          <span
                            className={
                              thumbnail ? "text-green-600" : "text-gray-400"
                            }
                          >
                            {thumbnail ? "✓ Selecionada" : "Opcional"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Título:</span>
                          <span
                            className={
                              input.title ? "text-green-600" : "text-gray-400"
                            }
                          >
                            {input.title ? "✓ Preenchido" : "Obrigatório"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Descrição:</span>
                          <span
                            className={
                              input.description
                                ? "text-green-600"
                                : "text-gray-400"
                            }
                          >
                            {input.description ? "✓ Preenchida" : "Obrigatória"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <TextField
                      required
                      label="Título da Lição"
                      name="title"
                      value={input.title}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                      error={!!input.errors.title}
                      helperText={input.errors.title}
                      disabled={loading.loading}
                      fullWidth
                      size="small"
                    />

                    {moduleId && modules && (
                      <div className="space-y-2 mb-6">
                        <label className="text-sm">Módulo</label>
                        <Select
                          value={moduleId}
                          onChange={onModuleIdChange}
                          disabled={loading.loading}
                          fullWidth
                          size="small"
                        >
                          {modules.map(({ id, name }) => (
                            <MenuItem
                              disabled={loading.loading}
                              key={id}
                              value={id}
                            >
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                    )}

                    <TextField
                      required
                      minRows={4}
                      maxRows={8}
                      multiline
                      label="Descrição"
                      name="description"
                      value={input.description}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                      error={!!input.errors.description}
                      helperText={input.errors.description}
                      disabled={loading.loading}
                      fullWidth
                      size="small"
                    />
                    {/* Checkbox para mostrar/ocultar secção avançado */}
                    <div className="pt-4 pl-3 mt-4 border-t">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showAdvanced}
                            onCheckedChange={(checked) => {
                              setShowAdvanced(!!checked);
                            }}
                            disabled={loading.loading}
                            className="border-text mr-2 "
                          />
                        }
                        label="Mostrar opções avançadas"
                      />

                      {showAdvanced && (
                        <div className="mt-2">
                          <h4 className="text-base font-semibold mb-2">
                            Avançado
                          </h4>
                          <TextField
                            label="Posição da Lição (opcional)"
                            name="position"
                            type="number"
                            value={position}
                            onChange={(e) => {
                              const val = e.target.value;
                              setPosition(val === "" ? "" : Number(val));
                            }}
                            onBlur={() => {}}
                            disabled={loading.loading}
                            fullWidth
                            size="small"
                            inputProps={{ min: 0 }}
                            helperText="Defina a posição da lição no módulo (opcional)"
                          />
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>

              <div className="mt-4 gap-4 flex items-center justify-between border-t pt-4">
                <div className="flex-1">
                  {error && (
                    <Alert severity="error" className="text-sm">
                      {error}
                    </Alert>
                  )}
                </div>

                <div className="space-x-3">
                  <Button
                    disabled={loading.loading}
                    variant="outline"
                    type="reset"
                    onClick={closeModal}
                    className="min-w-[120px] py-3 h-fit"
                    size="sm"
                  >
                    Cancelar
                  </Button>

                  <Button
                    disabled={loading.loading || !video}
                    type="submit"
                    className="min-w-[120px] py-3 h-fit"
                    size="sm"
                  >
                    Criar Lição
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </Fade>
    </Modal>
  );
}
